import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Api from "../../../constants/Api";
import axiosInstance from "../../../services/AxiosInstance";
import { errorAlert, successAlert, warnAlert } from "../alert/defaultAlert";
import HintTooltip from "../Common/HintTooltip";
import ColorField from "../Forms/Element/ColorField";
import { getImageFramingStyle } from "../../../utils/ImageFramingUtils";
import PublicIntroSetting from "./PublicIntroSetting";
import "./BrandingSetting.css";

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const MAX_BANNER_SIZE = 5 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];

const HEX_REGEX = /^#[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/;

const resolveApiUrl = (path) => {
    if (!path) {
        return null;
    }

    if (/^https?:\/\//i.test(path)) {
        return path;
    }

    const baseUrl = process.env.REACT_APP_API_URL || "";
    return `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;
};

const getPublicCompanyLogoUrl = (companyUuid) => {
    if (!companyUuid) {
        return null;
    }

    return resolveApiUrl(`/api/booking/${companyUuid}/logo`);
};

const getPublicCompanyBannerUrl = (companyUuid) => {
    if (!companyUuid) {
        return null;
    }

    return resolveApiUrl(`/api/booking/${companyUuid}/banner`);
};

const BrandingSetting = () => {
    const { t } = useTranslation();
    const logoInputRef = useRef(null);
    const bannerInputRef = useRef(null);
    const selectedBannerFileRef = useRef(null);
    const [company, setCompany] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [selectedLogoFile, setSelectedLogoFile] = useState(null);
    const [selectedBannerFile, setSelectedBannerFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savingLogo, setSavingLogo] = useState(false);
    const [savingBanner, setSavingBanner] = useState(false);
    const [headerColor, setHeaderColor] = useState("");
    const [bannerPositionX, setBannerPositionX] = useState(50);
    const [bannerPositionY, setBannerPositionY] = useState(50);
    const [bannerZoom, setBannerZoom] = useState(100);
    const [heroSubtitle, setHeroSubtitle] = useState("");
    const [showLogoOnBookingPage, setShowLogoOnBookingPage] = useState(true);
    const [showCompanyNameOnBookingPage, setShowCompanyNameOnBookingPage] = useState(true);
    const [savingBranding, setSavingBranding] = useState(false);
    const hasLoadedCompanyRef = useRef(false);

    const loadLogoPreview = useCallback((companyData = null) => {
        const publicLogoUrl = getPublicCompanyLogoUrl(companyData?.uuid || company?.uuid);

        return axiosInstance.get(Api.registration.company_logo, {
            responseType: "blob",
            params: { t: Date.now() },
        })
            .then((resp) => {
                setLogoPreview(URL.createObjectURL(resp.data));
            })
            .catch(() => {
                setLogoPreview(publicLogoUrl);
            });
    }, [company?.uuid]);

    const loadBannerPreview = useCallback((companyData = null) => {
        const publicBannerUrl = getPublicCompanyBannerUrl(companyData?.uuid || company?.uuid);

        return axiosInstance.get(Api.registration.company_banner, {
            responseType: "blob",
            params: { t: Date.now() },
        })
            .then((resp) => {
                // A resposta da imagem salva não pode sobrescrever uma nova seleção ainda não salva.
                if (selectedBannerFileRef.current) {
                    return;
                }
                setBannerPreview(URL.createObjectURL(resp.data));
            })
            .catch(() => {
                if (!selectedBannerFileRef.current) {
                    setBannerPreview(publicBannerUrl);
                }
            });
    }, [company?.uuid]);

    const loadCompanyInfo = useCallback(() => {
        /*
         * O spinner só aparece na primeira carga. Recarregar depois de salvar a logo ou o banner
         * desmontaria a tela inteira e perderia o que estiver em edição nos cards da página pública.
         */
        if (!hasLoadedCompanyRef.current) {
            setLoading(true);
        }
        axiosInstance.get(Api.registration.company_info)
            .then((resp) => {
                setCompany(resp.data);
                setCompanyName(resp.data?.name || "");
                setHeaderColor(resp.data?.headerColor || "");
                setBannerPositionX(resp.data?.bannerPositionX ?? 50);
                setBannerPositionY(resp.data?.bannerPositionY ?? 50);
                setBannerZoom(resp.data?.bannerZoom ?? 100);
                setHeroSubtitle(resp.data?.heroSubtitle || "");
                setShowLogoOnBookingPage(resp.data?.showLogoOnBookingPage !== false);
                setShowCompanyNameOnBookingPage(resp.data?.showCompanyNameOnBookingPage !== false);
                if (resp.data?.hasLogo) {
                    loadLogoPreview(resp.data);
                } else {
                    setLogoPreview(null);
                }

                if (resp.data?.hasBanner) {
                    return loadBannerPreview(resp.data);
                }
                setBannerPreview(null);
                return null;
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                hasLoadedCompanyRef.current = true;
                setLoading(false);
            });
    }, [loadBannerPreview, loadLogoPreview]);

    useEffect(() => {
        loadCompanyInfo();
    }, [loadCompanyInfo]);

    useEffect(() => {
        return () => {
            if (logoPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(logoPreview);
            }
            if (bannerPreview?.startsWith("blob:")) {
                URL.revokeObjectURL(bannerPreview);
            }
        };
    }, [bannerPreview, logoPreview]);

    const handleLogoChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!LOGO_TYPES.includes(file.type)) {
            warnAlert("A logo deve ser PNG, JPG ou WEBP.");
            event.target.value = "";
            return;
        }

        if (file.size > MAX_LOGO_SIZE) {
            warnAlert("A logo deve ter no máximo 2MB.");
            event.target.value = "";
            return;
        }

        if (logoPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(logoPreview);
        }

        setSelectedLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleBannerChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!LOGO_TYPES.includes(file.type)) {
            warnAlert("O banner deve ser PNG, JPG ou WEBP.");
            event.target.value = "";
            return;
        }

        if (file.size > MAX_BANNER_SIZE) {
            warnAlert("O banner deve ter no máximo 5MB.");
            event.target.value = "";
            return;
        }

        if (bannerPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(bannerPreview);
        }

        setSelectedBannerFile(file);
        selectedBannerFileRef.current = file;
        setBannerPreview(URL.createObjectURL(file));
    };

    const handleBannerHorizontalPositionChange = (event) => {
        const nextPosition = Number(event.target.value);
        setBannerPositionX(nextPosition);
        // Em zoom 100%, banners largos não têm sobra lateral para deslocar; cria espaço de recorte ao arrastar.
        setBannerZoom((currentZoom) => currentZoom <= 100 ? 125 : currentZoom);
    };

    const saveLogo = async () => {
        if (!selectedLogoFile) {
            warnAlert("Selecione uma logo antes de salvar.");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedLogoFile);

        setSavingLogo(true);
        axiosInstance.post(Api.registration.company_logo, formData, {
            headers: { "content-type": "multipart/form-data" },
        })
            .then(async () => {
                setSelectedLogoFile(null);
                if (logoInputRef.current) {
                    logoInputRef.current.value = "";
                }
                await loadCompanyInfo();
                successAlert(t("message.data_updated_successfully"));
            })
            .catch(error => errorAlert(error))
            .finally(() => setSavingLogo(false));
    };

    const removeLogo = async () => {
        setSavingLogo(true);
        axiosInstance.delete(Api.registration.company_logo)
            .then(async () => {
                setSelectedLogoFile(null);
                if (logoInputRef.current) {
                    logoInputRef.current.value = "";
                }
                await loadCompanyInfo();
                successAlert(t("message.data_updated_successfully"));
            })
            .catch(error => errorAlert(error))
            .finally(() => setSavingLogo(false));
    };

    const saveBanner = async () => {
        if (!selectedBannerFile) {
            warnAlert("Selecione um banner antes de salvar.");
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedBannerFile);

        setSavingBanner(true);
        axiosInstance.post(Api.registration.company_banner, formData, {
            headers: { "content-type": "multipart/form-data" },
        })
            .then(async () => {
                setSelectedBannerFile(null);
                selectedBannerFileRef.current = null;
                if (bannerInputRef.current) {
                    bannerInputRef.current.value = "";
                }
                await loadCompanyInfo();
                successAlert(t("message.data_updated_successfully"));
            })
            .catch(error => errorAlert(error))
            .finally(() => setSavingBanner(false));
    };

    const removeBanner = async () => {
        setSavingBanner(true);
        axiosInstance.delete(Api.registration.company_banner)
            .then(async () => {
                setSelectedBannerFile(null);
                selectedBannerFileRef.current = null;
                if (bannerInputRef.current) {
                    bannerInputRef.current.value = "";
                }
                await loadCompanyInfo();
                successAlert(t("message.data_updated_successfully"));
            })
            .catch(error => errorAlert(error))
            .finally(() => setSavingBanner(false));
    };

    const saveBranding = () => {
        const normalizedCompanyName = companyName.trim();
        if (normalizedCompanyName.length < 3 || normalizedCompanyName.length > 50) {
            warnAlert("O nome fantasia deve ter entre 3 e 50 caracteres.");
            return;
        }

        if (headerColor && !HEX_REGEX.test(headerColor)) {
            warnAlert("Cor inválida. Use o formato hexadecimal (ex: #1a202c).");
            return;
        }

        setSavingBranding(true);
        axiosInstance.patch(Api.registration.company_branding, {
            name: normalizedCompanyName,
            headerColor: headerColor || null,
            heroSubtitle: heroSubtitle || null,
            showLogoOnBookingPage,
            showCompanyNameOnBookingPage,
            bannerPositionX,
            bannerPositionY,
            bannerZoom,
        })
            .then(async (response) => {
                // Só confirma a alteração quando a API devolve o nome salvo; uma versão antiga
                // do backend poderia ignorar o campo novo e ainda responder sucesso ao PATCH.
                if (response.data?.name !== normalizedCompanyName) {
                    throw new Error("O servidor não confirmou a atualização do nome fantasia. Atualize o backend e tente novamente.");
                }
                await loadCompanyInfo();
                successAlert(t("message.data_updated_successfully"));
            })
            .catch(error => errorAlert(error))
            .finally(() => setSavingBranding(false));
    };

    return (
        <div className="card branding-setting">
            <div className="card-body">
                <h3 className="mb-2">Identidade visual</h3>
                <p className="text-muted mb-4">
                    Configure logo, banner, subtítulo, cores e os cards de apresentação exibidos nas telas públicas de agendamento.
                </p>

                {loading ? (
                    <div className="branding-loading text-center py-4" role="status" aria-live="polite">
                        <Spinner animation="border" />
                        <span className="visually-hidden">Carregando identidade visual…</span>
                    </div>
                ) : (
                    <>
                        <div className="branding-save-bar">
                            <span className="text-muted small">Nome e aparência são salvos juntos. Logo, banner e cards da apresentação têm salvamento próprio.</span>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={saveBranding}
                                disabled={savingBranding || (
                                    companyName.trim() === (company?.name || "") &&
                                    headerColor === (company?.headerColor || "") &&
                                    heroSubtitle === (company?.heroSubtitle || "") &&
                                    showLogoOnBookingPage === (company?.showLogoOnBookingPage !== false) &&
                                    showCompanyNameOnBookingPage === (company?.showCompanyNameOnBookingPage !== false) &&
                                    bannerPositionX === (company?.bannerPositionX ?? 50) &&
                                    bannerPositionY === (company?.bannerPositionY ?? 50) &&
                                    bannerZoom === (company?.bannerZoom ?? 100)
                                )}
                            >
                                {savingBranding ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa-solid fa-floppy-disk me-2"></i>}
                                Salvar alterações
                            </button>
                        </div>
                        <div className="branding-sections-grid">
                        <section className="branding-section branding-section--name">
                            <div className="branding-section__heading">
                                <span className="branding-section__icon"><i className="fa-solid fa-store" aria-hidden="true" /></span>
                                <div>
                                    <h5 className="mb-1">Nome fantasia</h5>
                                    <p className="text-muted mb-0">Este nome aparece na página pública de agendamento.</p>
                                </div>
                            </div>
                        <div className="branding-field branding-field--trade-name">
                            <label htmlFor="companyTradeName" className="form-label">Nome fantasia</label>
                            <input
                                id="companyTradeName"
                                type="text"
                                className="form-control"
                                placeholder="Nome da empresa"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value.slice(0, 50))}
                                maxLength={50}
                            />
                        </div>
                        </section>

                        {/* Logo */}
                        <section className="branding-section branding-section--logo">
                        <div className="branding-section__heading">
                            <span className="branding-section__icon"><i className="fa-regular fa-image" aria-hidden="true" /></span>
                            <div>
                                <h5 className="mb-1">Logo da empresa</h5>
                                <p className="text-muted mb-0">Use uma imagem quadrada ou horizontal, com fundo transparente se possível.</p>
                            </div>
                            <span className={`branding-asset-status${selectedLogoFile ? " is-pending" : company?.hasLogo ? " is-saved" : " is-empty"}`}>
                                {selectedLogoFile ? "Nova logo selecionada" : company?.hasLogo ? "Logo salva" : "Sem logo salva"}
                            </span>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-4 mb-4">
                            <div
                                className="branding-logo-preview d-flex align-items-center justify-content-center"
                            >
                                {logoPreview ? (
                                    <img
                                        className="branding-logo-preview__image"
                                        src={logoPreview}
                                        alt={company?.name || "Logo"}
                                        onError={() => setLogoPreview(null)}
                                    />
                                ) : (
                                    <div className="branding-logo-preview__empty text-muted px-3">
                                        <i className="fa-solid fa-image fa-2x mb-2 d-block"></i>
                                        <span>Sem logo</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex-grow-1">
                                <label htmlFor="companyLogo" className="form-label">
                                    Selecionar arquivo <HintTooltip text="Formatos aceitos: PNG, JPG ou WEBP. Tamanho máximo: 2MB." />
                                </label>
                                <input
                                    ref={logoInputRef}
                                    id="companyLogo"
                                    type="file"
                                    className="form-control"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={handleLogoChange}
                                />
                            </div>
                        </div>

                        <div className="branding-action-row d-flex gap-2 mb-2">
                            <button type="button" className="btn btn-primary" onClick={saveLogo} disabled={savingLogo || !selectedLogoFile}>
                                {savingLogo ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa-solid fa-floppy-disk me-2"></i>}
                                Salvar logo
                            </button>
                            <button type="button" className="btn btn-outline-danger" onClick={removeLogo} disabled={savingLogo || (!company?.hasLogo && !selectedLogoFile)}>
                                <i className="fa-solid fa-trash me-2"></i>
                                Remover logo
                            </button>
                        </div>
                        </section>

                        <section className="branding-section branding-section--banner">
                        <div className="branding-section__heading">
                            <span className="branding-section__icon"><i className="fa-regular fa-images" aria-hidden="true" /></span>
                            <div>
                                <h5 className="mb-1">Banner do agendamento</h5>
                                <p className="text-muted mb-0">Uma imagem horizontal ajuda a destacar a página da sua empresa.</p>
                            </div>
                            <span className={`branding-asset-status${selectedBannerFile ? " is-pending" : company?.hasBanner ? " is-saved" : " is-empty"}`}>
                                {selectedBannerFile ? "Novo banner selecionado" : company?.hasBanner ? "Banner salvo" : "Sem banner salvo"}
                            </span>
                        </div>
                        <div className="branding-field branding-field--banner">
                            <label htmlFor="companyBanner" className="form-label">
                                Selecionar banner <HintTooltip text="Formatos aceitos: PNG, JPG ou WEBP. Tamanho máximo: 5MB. Recomendado: imagem horizontal." />
                            </label>
                            <input
                                ref={bannerInputRef}
                                id="companyBanner"
                                type="file"
                                className="form-control"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleBannerChange}
                            />
                        </div>

                        <div className="branding-banner-preview-block mt-3 mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="form-label mb-0">Prévia e enquadramento</span>
                                {selectedBannerFile && <span className="text-muted small">Prévia da imagem selecionada</span>}
                            </div>
                            <div className="branding-banner-preview branding-banner-preview__frame overflow-hidden">
                                {bannerPreview ? (
                                    <img
                                        src={bannerPreview}
                                        alt={company?.name || "Prévia do banner"}
                                        style={getImageFramingStyle({ positionX: bannerPositionX, positionY: bannerPositionY, zoom: bannerZoom })}
                                        onError={() => setBannerPreview(null)}
                                    />
                                ) : (
                                    <div className="text-center text-muted h-100 d-flex flex-column justify-content-center px-3">
                                        <i className="fa-solid fa-panorama fa-2x mb-2 d-block" aria-hidden="true"></i>
                                        <span>Selecione uma imagem para ver o enquadramento no agendamento.</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="branding-banner-position" aria-label="Ajustar enquadramento do banner">
                            <div className="branding-banner-position__heading">
                                <strong>Ajustar enquadramento</strong>
                                <span>Arraste o botão circular para mover o foco. O ajuste horizontal aproxima a imagem quando necessário.</span>
                            </div>
                            <label htmlFor="bannerPositionY">Vertical <span>{bannerPositionY}%</span></label>
                            <input
                                id="bannerPositionY"
                                type="range"
                                min="0"
                                max="100"
                                value={bannerPositionY}
                                onChange={(event) => setBannerPositionY(Number(event.target.value))}
                                className="form-range"
                            />
                            <label htmlFor="bannerPositionX">Horizontal <span>{bannerPositionX}%</span></label>
                            <input
                                id="bannerPositionX"
                                type="range"
                                min="0"
                                max="100"
                                value={bannerPositionX}
                                onChange={handleBannerHorizontalPositionChange}
                                className="form-range"
                            />
                            <label htmlFor="bannerZoom">Zoom <span>{bannerZoom}%</span></label>
                            <input
                                id="bannerZoom"
                                type="range"
                                min="100"
                                max="200"
                                step="1"
                                value={bannerZoom}
                                onChange={(event) => setBannerZoom(Number(event.target.value))}
                                className="form-range"
                            />
                            <small>O enquadramento também aparece na prévia do cabeçalho abaixo. Salve as alterações para publicar.</small>
                        </div>

                        <div className="branding-action-row d-flex gap-2 mb-2">
                            <button type="button" className="btn btn-primary" onClick={saveBanner} disabled={savingBanner || !selectedBannerFile}>
                                {savingBanner ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fa-solid fa-floppy-disk me-2"></i>}
                                Salvar banner
                            </button>
                            <button type="button" className="btn btn-outline-danger" onClick={removeBanner} disabled={savingBanner || (!company?.hasBanner && !selectedBannerFile)}>
                                <i className="fa-solid fa-trash me-2"></i>
                                Remover banner
                            </button>
                        </div>
                        </section>

                        {/* Cor do header */}
                        <section className="branding-section branding-section--appearance">
                        <div className="branding-section__heading">
                            <span className="branding-section__icon"><i className="fa-solid fa-palette" aria-hidden="true" /></span>
                            <div>
                                <h5 className="mb-1">Aparência da página</h5>
                                <p className="text-muted mb-0">Ajuste a cor e o conteúdo exibidos no cabeçalho do agendamento.</p>
                            </div>
                        </div>
                        <h6 className="mb-1">Cor do cabeçalho</h6>
                        <p className="text-muted mb-3" style={{ fontSize: "0.875rem" }}>
                            Cor de fundo do cabeçalho nas telas públicas. Deixe em branco para usar o padrão.
                        </p>

                        <div className="mb-4">
                            <ColorField
                                label="cor do cabeçalho"
                                value={headerColor}
                                fallback="#2D3748"
                                onChange={setHeaderColor}
                                onClear={() => setHeaderColor("")}
                            />
                        </div>

                        <div className="branding-field branding-field--subtitle">
                            <label htmlFor="heroSubtitle" className="form-label">
                                Subtítulo do hero <HintTooltip text="Texto exibido abaixo do nome da empresa na página pública de agendamento." />
                            </label>
                            <input
                                id="heroSubtitle"
                                type="text"
                                className="form-control"
                                placeholder="Ex: Reserve seu horário com quem entende do seu estilo"
                                value={heroSubtitle}
                                onChange={(e) => setHeroSubtitle(e.target.value.slice(0, 160))}
                                maxLength={160}
                            />
                        </div>

                        <div className="branding-display-options">
                        <div className="branding-display-option">
                            <div className="branding-display-option__copy">
                                <label className="branding-display-option__title" htmlFor="showLogoOnBookingPage">
                                    Mostrar logo da empresa <HintTooltip text="Desative se quiser usar apenas o banner e o nome da empresa no cabeçalho." />
                                </label>
                                <p className="branding-display-option__description">Exiba a logo junto ao cabeçalho da página pública de agendamento.</p>
                            </div>
                            <div className="form-check form-switch branding-display-option__switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showLogoOnBookingPage"
                                    checked={showLogoOnBookingPage}
                                    onChange={(e) => setShowLogoOnBookingPage(e.target.checked)}
                                />
                                <label className="visually-hidden" htmlFor="showLogoOnBookingPage">Mostrar logo da empresa</label>
                            </div>
                        </div>

                        <div className="branding-display-option">
                            <div className="branding-display-option__copy">
                                <label className="branding-display-option__title" htmlFor="showCompanyNameOnBookingPage">
                                    Mostrar nome fantasia <HintTooltip text="Desative se quiser exibir apenas o banner, a logo e o subtítulo no cabeçalho." />
                                </label>
                                <p className="branding-display-option__description">Exiba o nome da empresa no cabeçalho da página pública de agendamento.</p>
                            </div>
                            <div className="form-check form-switch branding-display-option__switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showCompanyNameOnBookingPage"
                                    checked={showCompanyNameOnBookingPage}
                                    onChange={(e) => setShowCompanyNameOnBookingPage(e.target.checked)}
                                />
                                <label className="visually-hidden" htmlFor="showCompanyNameOnBookingPage">Mostrar nome fantasia</label>
                            </div>
                        </div>
                        </div>

                        <div className="branding-preview-heading">
                            <h6 className="mb-1">Prévia da página pública</h6>
                            <p className="text-muted mb-2">Simulação do cabeçalho no agendamento, incluindo altura e corte do banner.</p>
                        </div>
                        <div
                            className="branding-hero-preview booking-hero mb-4"
                            style={{
                                background: headerColor || "linear-gradient(160deg, #2d3748 0%, #1a202c 100%)",
                            }}
                        >
                            {bannerPreview && (
                                <div
                                    className="booking-hero-banner-layer"
                                    aria-hidden="true"
                                    style={{
                                        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url(${bannerPreview})`,
                                        backgroundPosition: `${bannerPositionX}% ${bannerPositionY}%`,
                                        backgroundSize: "cover",
                                        transform: `scale(${bannerZoom / 100})`,
                                        transformOrigin: `${bannerPositionX}% ${bannerPositionY}%`,
                                    }}
                                />
                            )}
                            <div className={`booking-hero-content${!showLogoOnBookingPage ? " booking-hero-content--without-logo" : ""}`}>
                                <div className="booking-hero-copy">
                                    <div className="booking-hero-kicker">
                                        <i className="fa fa-calendar-check me-2" aria-hidden="true" />
                                        Agendamento online
                                    </div>
                                    {showCompanyNameOnBookingPage && (
                                        <h1 className="booking-hero-name">{companyName || "Nome da empresa"}</h1>
                                    )}
                                    <p className="booking-hero-sub">
                                        {heroSubtitle || "Agende seu atendimento em uma experiência com a cara da sua marca."}
                                    </p>
                                </div>
                                {showLogoOnBookingPage && (
                                    <div className="booking-hero-logo">
                                        {logoPreview && (
                                            <img
                                                src={logoPreview}
                                                alt="Prévia da logo"
                                                onError={() => setLogoPreview(null)}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        </section>

                        <PublicIntroSetting
                            company={company}
                            onSaved={loadCompanyInfo}
                            // A página /p/{slug} mostra a logo sempre, sem a opção do cabeçalho do agendamento.
                            logoSrc={logoPreview}
                            fallbackImageSrc={bannerPreview}
                        />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default BrandingSetting;

